/** Browser-only OCR. Copy bytes never leave the device. Runtime assets are same-origin. */
export const OCR_LIMITS = Object.freeze({ bytes: 10 * 1024 * 1024, pages: 5, pixels: 2000000, decodePixels: 25000000, characters: 100000, timeoutMs: 180000 });
const TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
const aborted = () => new DOMException('Extraction annulée.', 'AbortError');
export function validateOcrFile(file) {
  if (!file || !TYPES.includes(file.type)) throw new Error('Choisissez un fichier PNG, JPEG, WebP ou PDF.');
  if (!file.size) throw new Error('Ce fichier est vide.');
  if (file.size > OCR_LIMITS.bytes) throw new Error('Fichier trop volumineux : 10 Mo maximum.');
}
export function rasterSize(width, height) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) throw new Error('Dimensions de page invalides.');
  const scale = Math.min(1, Math.sqrt(OCR_LIMITS.pixels / (width * height)));
  return { width: Math.max(1, Math.floor(width * scale)), height: Math.max(1, Math.floor(height * scale)), scale };
}
export function readablePdfText(items) {
  const text = items.map(x => typeof x.str === 'string' ? x.str + (x.hasEOL ? '\n' : ' ') : '').join('').trim();
  // A short heading alone must not suppress OCR of the scanned body.
  const letters = (text.match(/[\p{L}\p{N}]/gu) || []).length;
  return letters >= 30 && !text.includes('\ufffd') ? text : '';
}
function assetRoot() {
  const base = new URL(`${process.env.PUBLIC_URL || ''}/local-ocr/`, window.location.href);
  if (base.origin !== window.location.origin) throw new Error('Les ressources OCR doivent être hébergées sur ce site.');
  return base.href;
}

/** Isolate the API in a controller so even Tesseract initialization can be cancelled. */
function browserOcrWorker(root, report) {
  const controller = new Worker(`${root}controller.js`);
  let resolveReady, rejectReady, resolveResult, rejectResult, dead = false;
  const ready = new Promise((resolve, reject) => { resolveReady = resolve; rejectReady = reject; });
  ready.catch(() => {});
  const fail = () => {
    const error = new Error('Le moteur OCR local n’a pas pu démarrer ou lire cette image. Vérifiez les fichiers du site et réessayez.');
    rejectReady(error); rejectResult?.(error);
  };
  controller.onerror = fail;
  controller.onmessage = ({ data }) => {
    if (dead) return;
    if (data.type === 'ready') resolveReady();
    else if (data.type === 'error') fail();
    else if (data.type === 'result') resolveResult?.({ data: { text: data.text } });
    else if (data.type === 'progress') report(data.progress.status === 'recognizing text' ? 'Reconnaissance du texte…' : 'Préparation du moteur local…', data.progress.progress);
  };
  controller.postMessage({ type: 'init' });
  return {
    async recognize(canvas) {
      await ready;
      if (dead) throw aborted();
      const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('Image illisible.')), 'image/png'));
      const bytes = await blob.arrayBuffer();
      if (dead) throw aborted();
      return new Promise((resolve, reject) => {
        resolveResult = resolve; rejectResult = reject;
        controller.postMessage({ type: 'recognize', bytes }, [bytes]);
      });
    },
    async terminate() {
      if (dead) return;
      dead = true; rejectReady(aborted()); rejectResult?.(aborted());
      // The controller terminates its owned child even while createWorker is still pending.
      controller.postMessage({ type: 'cancel' });
      setTimeout(() => controller.terminate(), 100);
    },
  };
}

/** Returns an operation immediately so callers can cancel and ignore stale results. */
export function startLocalOcr(file, { onProgress = () => {}, signal, dependencies = {} } = {}) {
  let worker, loadingTask, renderTask, bitmap, stopped = false;
  const canvases = new Set();
  let rejectStop;
  const stopPromise = new Promise((_, reject) => { rejectStop = reject; });
  const check = () => { if (stopped) throw aborted(); };
  const report = (message, progress) => { if (!stopped) onProgress({ message, progress }); };
  const cleanup = () => {
    if (worker) { void worker.terminate().catch(() => {}); worker = null; }
    if (renderTask) { renderTask.cancel(); renderTask = null; }
    if (loadingTask) { void loadingTask.destroy().catch(() => {}); loadingTask = null; }
    bitmap?.close(); bitmap = null;
    for (const c of canvases) { c.width = 0; c.height = 0; }
    canvases.clear();
  };
  const cancel = (reason = aborted()) => {
    if (stopped) return;
    stopped = true; cleanup(); rejectStop(reason);
  };
  const abortListener = () => cancel();
  const timer = setTimeout(() => cancel(new Error('Extraction trop longue. Réessayez avec une image plus petite ou une seule page.')), OCR_LIMITS.timeoutMs);
  signal?.addEventListener('abort', abortListener, { once: true });
  const canvas = (width, height) => {
    const c = document.createElement('canvas'); c.width = width; c.height = height; canvases.add(c); return c;
  };
  const recognize = async (image) => {
    check();
    if (!worker) {
      report('Chargement du moteur français local…');
      const root = assetRoot();
      const created = dependencies.createWorker
        ? await dependencies.createWorker('fra', 1, { logger: ({ status, progress }) => report(status, progress) })
        : browserOcrWorker(root, report);
      if (stopped) { await created.terminate(); throw aborted(); }
      worker = created;
    }
    const result = await worker.recognize(image);
    check(); return result.data.text.trim();
  };
  const run = async () => {
    validateOcrFile(file); check();
    let text = '', pages = 1, method = 'ocr';
    if (file.type === 'application/pdf') {
      report('Lecture du PDF sur cet appareil…');
      const root = assetRoot();
      const pdfjs = dependencies.pdfjs || await import(/* webpackIgnore: true */ `${root}pdf.mjs`);
      check(); pdfjs.GlobalWorkerOptions.workerSrc = `${root}pdf.worker.mjs`;
      const data = new Uint8Array(await file.arrayBuffer()); check();
      loadingTask = pdfjs.getDocument({ data, isEvalSupported: false, stopAtErrors: true, maxImageSize: OCR_LIMITS.decodePixels, canvasMaxAreaInBytes: OCR_LIMITS.pixels * 4, cMapUrl: `${root}pdf/cmaps/`, cMapPacked: true, standardFontDataUrl: `${root}pdf/standard_fonts/`, wasmUrl: `${root}pdf/wasm/`, useWorkerFetch: false });
      const pdf = await loadingTask.promise; check();
      if (pdf.numPages > OCR_LIMITS.pages) throw new Error('PDF limité à 5 pages. Divisez le document avant de réessayer.');
      pages = pdf.numPages; const texts = []; let ocrPages = 0;
      for (let n = 1; n <= pages; n += 1) {
        check(); report(`Lecture de la page ${n}/${pages}…`);
        const page = await pdf.getPage(n); check();
        const content = await page.getTextContent(); check();
        let pageText = readablePdfText(content.items);
        if (!pageText) {
          ocrPages += 1;
          const viewport = page.getViewport({ scale: 2 });
          const size = rasterSize(viewport.width, viewport.height);
          const view = page.getViewport({ scale: 2 * size.scale });
          const c = canvas(size.width, size.height);
          renderTask = page.render({ canvasContext: c.getContext('2d'), viewport: view });
          await renderTask.promise; renderTask = null; check();
          pageText = await recognize(c);
          c.width = 0; c.height = 0; canvases.delete(c);
        }
        texts.push(pageText); page.cleanup();
        if (texts.join('\n\n').length > OCR_LIMITS.characters) throw new Error('Texte trop long : 100 000 caractères maximum.');
      }
      text = texts.join('\n\n'); method = ocrPages === 0 ? 'pdf-text' : ocrPages === pages ? 'ocr' : 'mixed';
    } else {
      report('Préparation de l’image…');
      const decode = dependencies.createImageBitmap || window.createImageBitmap?.bind(window);
      if (!decode) throw new Error('Ce navigateur ne prend pas en charge la lecture locale des images. Essayez une version récente de Firefox, Chrome ou Safari.');
      const decoded = await decode(file);
      if (stopped) { decoded.close(); throw aborted(); }
      bitmap = decoded;
      if (bitmap.width * bitmap.height > OCR_LIMITS.decodePixels) throw new Error('Image trop grande : 25 mégapixels maximum. Réduisez ses dimensions.');
      const size = rasterSize(bitmap.width, bitmap.height), c = canvas(size.width, size.height);
      c.getContext('2d').drawImage(bitmap, 0, 0, size.width, size.height); bitmap.close(); bitmap = null;
      text = await recognize(c);
    }
    check();
    if (!text.trim()) throw new Error('Aucun texte lisible trouvé. Essayez une photo nette, droite et bien éclairée.');
    if (text.length > OCR_LIMITS.characters) throw new Error('Texte trop long : 100 000 caractères maximum.');
    return { text, pages, method };
  };
  if (signal?.aborted) cancel();
  const promise = Promise.race([run(), stopPromise]).finally(() => {
    stopped = true; clearTimeout(timer); signal?.removeEventListener('abort', abortListener); cleanup();
  });
  return { promise, cancel };
}
